// DocumentValidator utility for comprehensive document validation and metadata extraction
// Supports expiration dates, document numbers, issuing authorities, and file quality checks

import { getDocument, PDFDocumentProxy } from 'pdfjs-dist'
import { DocumentType, DocumentStatus, DocumentMetadata, DocumentValidationRule } from './types'

// PDF.js metadata types (custom interfaces for type safety)
interface PDFInfo {
  Title?: string;
  Author?: string;
  Subject?: string;
  Keywords?: string;
  Creator?: string;
  Producer?: string;
  CreationDate?: string;
  ModDate?: string;
  [key: string]: any; // Allow other PDF metadata properties
}

interface PDFMetadataResponse {
  info: PDFInfo;
  metadata?: any;
}

// Validation result interface
export interface ValidationResult {
  isValid: boolean
  status: DocumentStatus
  errors: string[]
  warnings: string[]
  metadata?: DocumentMetadata
}

// Document format specifications
export interface DocumentFormat {
  documentType: DocumentType
  numberPattern: RegExp
  requiredFields: string[]
  keywords: string[]
  issuingAuthorities?: string[]
  expirationRequired: boolean
}

// Image quality check result
export interface ImageQualityResult {
  dpi: number
  width: number
  height: number
  fileSize: number
  isOCRReady: boolean
}

// Predefined document formats
export const DOCUMENT_FORMATS: Record<DocumentType, DocumentFormat> = {
  [DocumentType.LICENSE]: {
    documentType: DocumentType.LICENSE,
    numberPattern: /^[A-Z0-9]{6,15}$/,
    requiredFields: ['documentNumber', 'expirationDate', 'issuingAuthority'],
    keywords: ['license', 'permit', 'certification', 'authorized', 'valid'],
    issuingAuthorities: ['DMV', 'DOT', 'State of', 'Department of'],
    expirationRequired: true
  },
  [DocumentType.CERTIFICATE]: {
    documentType: DocumentType.CERTIFICATE,
    numberPattern: /^[A-Z0-9\-]{8,20}$/,
    requiredFields: ['documentNumber', 'issuingAuthority'],
    keywords: ['certificate', 'certification', 'certified', 'diploma', 'award'],
    issuingAuthorities: ['University', 'Institute', 'Board', 'Academy'],
    expirationRequired: false
  },
  [DocumentType.PERMIT]: {
    documentType: DocumentType.PERMIT,
    numberPattern: /^[A-Z0-9\-]{6,15}$/,
    requiredFields: ['documentNumber', 'expirationDate', 'issuingAuthority'],
    keywords: ['permit', 'authorization', 'approval', 'permission', 'allowed'],
    issuingAuthorities: ['City of', 'County of', 'State of', 'Department'],
    expirationRequired: true
  },
  [DocumentType.INSURANCE]: {
    documentType: DocumentType.INSURANCE,
    numberPattern: /^[A-Z0-9\-]{8,25}$/,
    requiredFields: ['documentNumber', 'expirationDate'],
    keywords: ['insurance', 'policy', 'coverage', 'insured', 'premium'],
    issuingAuthorities: ['Insurance Company', 'Corp', 'Inc', 'LLC'],
    expirationRequired: true
  },
  [DocumentType.AGREEMENT]: {
    documentType: DocumentType.AGREEMENT,
    numberPattern: /^[A-Z0-9\-]{6,20}$/,
    requiredFields: ['documentNumber'],
    keywords: ['agreement', 'contract', 'terms', 'conditions', 'signed'],
    expirationRequired: false
  },
  [DocumentType.OTHER]: {
    documentType: DocumentType.OTHER,
    numberPattern: /^.+$/,
    requiredFields: [],
    keywords: [],
    expirationRequired: false
  },
  [DocumentType.PDF]: {
    documentType: DocumentType.PDF,
    numberPattern: /^.+$/,
    requiredFields: [],
    keywords: [],
    expirationRequired: false
  }
}

export class DocumentValidator {
  private static instance: DocumentValidator

  static getInstance(): DocumentValidator {
    if (!this.instance) {
      this.instance = new DocumentValidator()
    }
    return this.instance
  }

  // Validate document comprehensively
  async validateDocument(
    file: File, 
    documentType: DocumentType,
    customRules: DocumentValidationRule[] = []
  ): Promise<ValidationResult> {
    const errors: string[] = []
    const warnings: string[] = []
    let metadata: DocumentMetadata | undefined

    try {
      // Extract metadata first
      metadata = await this.extractMetadata(file)

      // Basic file validation
      const basicValidation = this.validateBasicFile(file)
      if (!basicValidation.isValid) {
        errors.push(...basicValidation.errors)
      }
      warnings.push(...basicValidation.warnings)

      // Document type specific validation
      const typeValidation = await this.validateDocumentType(file, documentType, metadata)
      if (!typeValidation.isValid) {
        errors.push(...typeValidation.errors)
      }
      warnings.push(...typeValidation.warnings)

      // Image quality validation for scanned documents
      if (file.type.startsWith('image/')) {
        const qualityValidation = await this.validateImageQuality(file)
        if (!qualityValidation.isOCRReady) {
          warnings.push(`Image quality may not be suitable for OCR (${qualityValidation.dpi} DPI)`)
        }
      }

      // Run custom validation rules
      for (const rule of customRules) {
        const result = rule.validate(file, metadata)
        if (typeof result === 'string') {
          errors.push(result)
        } else if (!result) {
          errors.push(rule.message)
        }
      }

      // Determine status
      let status = DocumentStatus.VALID
      if (errors.length > 0) {
        status = DocumentStatus.REJECTED
      } else if (warnings.length > 0) {
        status = DocumentStatus.PENDING_REVIEW
      }

      return {
        isValid: errors.length === 0,
        status,
        errors,
        warnings,
        metadata
      }

    } catch (error) {
      console.error('Document validation error:', error)
      return {
        isValid: false,
        status: DocumentStatus.REJECTED,
        errors: ['Failed to validate document'],
        warnings: []
      }
    }
  }

  // Extract comprehensive metadata from PDF
  async extractMetadata(file: File): Promise<DocumentMetadata> {
    const metadata: DocumentMetadata = {
      id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: file.name,
      fileSize: file.size,
      createdDate: new Date(file.lastModified)
    }

    if (file.type === 'application/pdf') {
      try {
        const arrayBuffer = await file.arrayBuffer()
        const pdf = await getDocument({ data: arrayBuffer }).promise
        
        metadata.pageCount = pdf.numPages
        
        // Extract PDF metadata
        const pdfMetadata = await pdf.getMetadata() as PDFMetadataResponse
        if (pdfMetadata.info) {
          const info = pdfMetadata.info
          metadata.title = info.Title || file.name
          metadata.author = info.Author || undefined
          
          if (info.CreationDate) {
            metadata.createdDate = new Date(info.CreationDate)
          }
        }

        // Extract text for keyword analysis
        const keywords = await this.extractKeywordsFromPDF(pdf)
        metadata.keywords = keywords

        // Try to extract document number using OCR patterns
        const documentNumber = await this.extractDocumentNumber(pdf)
        if (documentNumber) {
          metadata.documentNumber = documentNumber
        }

      } catch (error) {
        console.warn('PDF metadata extraction failed:', error)
      }
    } else if (file.type.startsWith('image/')) {
      // Extract image metadata
      const qualityInfo = await this.validateImageQuality(file)
      metadata.dpi = qualityInfo.dpi
    }

    return metadata
  }

  // Extract keywords from PDF text
  private async extractKeywordsFromPDF(pdf: PDFDocumentProxy): Promise<string[]> {
    const keywords: Set<string> = new Set()
    const maxPages = Math.min(pdf.numPages, 3) // Only check first 3 pages for performance

    try {
      for (let i = 1; i <= maxPages; i++) {
        const page = await pdf.getPage(i)
        const textContent = await page.getTextContent()
        const text = textContent.items.map((item: any) => item.str).join(' ').toLowerCase()

        // Extract potential keywords
        const words = text.split(/\s+/).filter(word => word.length > 3)
        words.forEach(word => {
          if (this.isRelevantKeyword(word)) {
            keywords.add(word)
          }
        })
      }
    } catch (error) {
      console.warn('Failed to extract PDF text:', error)
    }

    return Array.from(keywords)
  }

  // Extract document number using common patterns
  private async extractDocumentNumber(pdf: PDFDocumentProxy): Promise<string | undefined> {
    const patterns = [
      /(?:number|no|#)\s*:?\s*([A-Z0-9\-]{6,20})/i,
      /([A-Z]{2,4}\d{6,12})/,
      /(\d{2,4}[-\s]\d{3,6}[-\s]\d{3,6})/
    ]

    try {
      const page = await pdf.getPage(1)
      const textContent = await page.getTextContent()
      const text = textContent.items.map((item: any) => item.str).join(' ')

      for (const pattern of patterns) {
        const match = text.match(pattern)
        if (match) {
          return match[1].replace(/\s/g, '')
        }
      }
    } catch (error) {
      console.warn('Failed to extract document number:', error)
    }

    return undefined
  }

  // Validate basic file properties
  private validateBasicFile(file: File): Pick<ValidationResult, 'isValid' | 'errors' | 'warnings'> {
    const errors: string[] = []
    const warnings: string[] = []

    // File size validation (25MB max for documents)
    if (file.size > 25 * 1024 * 1024) {
      errors.push('File size exceeds 25MB limit')
    } else if (file.size > 10 * 1024 * 1024) {
      warnings.push('Large file size may slow down processing')
    }

    // File type validation
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/tiff'
    ]

    if (!allowedTypes.includes(file.type)) {
      errors.push('Unsupported file type. Please upload PDF, DOC, DOCX, JPG, PNG, or TIFF files')
    }

    // Empty file check
    if (file.size === 0) {
      errors.push('File is empty')
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }

  // Validate document type specific requirements
  private async validateDocumentType(
    file: File, 
    documentType: DocumentType,
    metadata: DocumentMetadata
  ): Promise<Pick<ValidationResult, 'isValid' | 'errors' | 'warnings'>> {
    const errors: string[] = []
    const warnings: string[] = []
    
    const format = DOCUMENT_FORMATS[documentType]
    if (!format) {
      errors.push('Unknown document type')
      return { isValid: false, errors, warnings }
    }

    // Document number validation
    if (metadata.documentNumber) {
      if (!format.numberPattern.test(metadata.documentNumber)) {
        errors.push(`Document number format invalid for ${documentType}`)
      }
    } else if (format.requiredFields.includes('documentNumber')) {
      warnings.push('Document number not detected')
    }

    // Expiration date validation
    if (format.expirationRequired && !metadata.expirationDate) {
      warnings.push('Expiration date required but not detected')
    } else if (metadata.expirationDate) {
      const now = new Date()
      if (metadata.expirationDate < now) {
        errors.push('Document has expired')
      } else if (metadata.expirationDate.getTime() - now.getTime() < 30 * 24 * 60 * 60 * 1000) {
        warnings.push('Document expires within 30 days')
      }
    }

    // Keyword relevance check
    if (metadata.keywords && metadata.keywords.length > 0) {
      const hasRelevantKeywords = format.keywords.some(keyword =>
        metadata.keywords?.some(k => k.includes(keyword.toLowerCase()))
      )
      if (!hasRelevantKeywords) {
        warnings.push(`Document content may not match ${documentType} type`)
      }
    }

    // Issuing authority check
    if (format.issuingAuthorities && metadata.issuingAuthority) {
      const hasValidAuthority = format.issuingAuthorities.some(authority =>
        metadata.issuingAuthority?.includes(authority)
      )
      if (!hasValidAuthority) {
        warnings.push('Issuing authority not recognized')
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }

  // Validate image quality for OCR readiness
  private async validateImageQuality(file: File): Promise<ImageQualityResult> {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        // Estimate DPI (simplified calculation)
        // Real DPI would need EXIF data extraction
        const estimatedDPI = Math.min(img.width, img.height) / 8 // Rough estimate
        
        const result: ImageQualityResult = {
          dpi: estimatedDPI,
          width: img.width,
          height: img.height,
          fileSize: file.size,
          isOCRReady: estimatedDPI >= 200 && img.width >= 800 && img.height >= 600
        }
        
        resolve(result)
      }
      
      img.onerror = () => {
        resolve({
          dpi: 0,
          width: 0,
          height: 0,
          fileSize: file.size,
          isOCRReady: false
        })
      }
      
      img.src = URL.createObjectURL(file)
    })
  }

  // Auto-detect document type based on content
  async autoDetectDocumentType(file: File): Promise<DocumentType> {
    try {
      const metadata = await this.extractMetadata(file)
      const fileName = file.name.toLowerCase()
      
      // Check filename first
      for (const [type, format] of Object.entries(DOCUMENT_FORMATS)) {
        if (format.keywords.some(keyword => fileName.includes(keyword))) {
          return type as DocumentType
        }
      }

      // Check content keywords
      if (metadata.keywords && metadata.keywords.length > 0) {
        for (const [type, format] of Object.entries(DOCUMENT_FORMATS)) {
          const keywordMatches = format.keywords.filter(keyword =>
            metadata.keywords?.some(k => k.includes(keyword.toLowerCase()))
          ).length
          
          if (keywordMatches >= 2) {
            return type as DocumentType
          }
        }
      }

    } catch (error) {
      console.warn('Auto-detection failed:', error)
    }

    return DocumentType.OTHER
  }

  // Check if a word is a relevant keyword
  private isRelevantKeyword(word: string): boolean {
    const relevantWords = [
      'license', 'permit', 'certificate', 'insurance', 'policy',
      'agreement', 'contract', 'authorization', 'approval', 'valid',
      'expired', 'issued', 'authority', 'department', 'state', 'number'
    ]
    return relevantWords.includes(word)
  }

  // Create validation rule builders
  static createExpirationRule(allowExpired: boolean = false): DocumentValidationRule {
    return {
      name: 'expiration-check',
      message: allowExpired ? 'Document expiration should be verified' : 'Document must not be expired',
      validate: (file: File, metadata?: DocumentMetadata) => {
        if (metadata?.expirationDate) {
          const isExpired = metadata.expirationDate < new Date()
          return allowExpired ? true : !isExpired
        }
        return true // No expiration date found
      }
    }
  }

  static createFileSizeRule(maxSizeMB: number): DocumentValidationRule {
    return {
      name: 'file-size',
      message: `File size must not exceed ${maxSizeMB}MB`,
      validate: (file: File) => {
        return file.size <= maxSizeMB * 1024 * 1024
      }
    }
  }

  static createDPIRule(minDPI: number): DocumentValidationRule {
    return {
      name: 'dpi-check',
      message: `Image must be at least ${minDPI} DPI for quality processing`,
      validate: (file: File, metadata?: DocumentMetadata) => {
        if (file.type.startsWith('image/') && metadata?.dpi) {
          return metadata.dpi >= minDPI
        }
        return true // Not an image or DPI not available
      }
    }
  }
}

export default DocumentValidator